import { collection, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { db } from "./firebase";

/**
 * The alerts strip.
 *
 * **Hidden entirely when there is nothing to say.** A widget that is present
 * and empty every day trains people to stop looking at that part of the screen,
 * and the one morning it is not empty is the morning nobody notices. An absent
 * strip and a full one are different shapes, which is what makes the full one
 * catch an eye.
 */

export type AlertKind =
  | "retentionOverdue"
  | "retentionMissed"
  | "aiBoundaryViolation"
  | "aiBudgetExhausted"
  | "legalGap";

export interface Alert {
  kind: AlertKind;
  /** i18n key. */
  messageKey: string;
  params?: Record<string, unknown>;
  /** Where to go and do something about it. */
  href: string;
}

export interface AlertInputs {
  /** The most recent retention run, if there is one. */
  lastRetentionRun?: { finishedAt?: string; overdueRemaining?: number; startedAt: string };
  aiRefusals: number;
  aiBudgetExhausted: number;
  /** Institutions with a required document that has never been published. */
  legalGaps: number;
  now: number;
}

/** A scheduled job that has not finished within a day has not run. */
export const MISSED_RUN_HOURS = 26;

/**
 * Pure, so the rules can be read without a database.
 *
 * Ordered by how quickly somebody has to act: a boundary violation is a
 * structural failure and comes first; a retention backlog is a compliance
 * clock; a legal gap is ours to fix and blocks a sale rather than a lesson.
 */
export function computeAlerts(inputs: AlertInputs): Alert[] {
  const alerts: Alert[] = [];

  if (inputs.aiRefusals > 0) {
    /*
      The guard refused to send something. It should be unreachable — the type
      union stops student data at compile time — so a refusal means something
      has gone structurally wrong and is a P1 rather than a statistic.
    */
    alerts.push({
      kind: "aiBoundaryViolation",
      messageKey: "alerts.aiBoundaryViolation",
      params: { count: inputs.aiRefusals },
      href: "/ai",
    });
  }

  const run = inputs.lastRetentionRun;
  if (run) {
    const started = Date.parse(run.startedAt);
    const stale = inputs.now - started > MISSED_RUN_HOURS * 3_600_000;

    // A job that crashed and a job that never started must not look the same:
    // the run document is written before the work begins, so one with no
    // `finishedAt` is a crash and an absent recent run is a miss.
    if (!run.finishedAt || stale) {
      alerts.push({ kind: "retentionMissed", messageKey: "alerts.retentionMissed", href: "/retention" });
    }
    if ((run.overdueRemaining ?? 0) > 0) {
      alerts.push({
        kind: "retentionOverdue",
        messageKey: "alerts.retentionOverdue",
        params: { count: run.overdueRemaining },
        href: "/retention",
      });
    }
  } else {
    alerts.push({ kind: "retentionMissed", messageKey: "alerts.retentionMissed", href: "/retention" });
  }

  if (inputs.aiBudgetExhausted > 0) {
    alerts.push({
      kind: "aiBudgetExhausted",
      messageKey: "alerts.aiBudgetExhausted",
      params: { count: inputs.aiBudgetExhausted },
      href: "/ai",
    });
  }

  if (inputs.legalGaps > 0) {
    alerts.push({
      kind: "legalGap",
      messageKey: "alerts.legalGap",
      params: { count: inputs.legalGaps },
      href: "/legal",
    });
  }

  return alerts;
}

export async function loadAlerts(now: number, legalGaps: number): Promise<Alert[]> {
  const since = new Date(now - 40 * 86_400_000).toISOString();

  const [runs, refusals, exhausted] = await Promise.all([
    getDocs(query(collection(db(), "retentionRuns"), orderBy("startedAt", "desc"), limit(1))),
    getDocs(query(collection(db(), "aiUsage"), where("outcome", "==", "refused"), where("at", ">=", since))),
    getDocs(
      query(collection(db(), "aiUsage"), where("outcome", "==", "budget_exceeded"), where("at", ">=", since))
    ),
  ]);

  const run = runs.docs[0]?.data() as AlertInputs["lastRetentionRun"];
  return computeAlerts({
    ...(run ? { lastRetentionRun: run } : {}),
    aiRefusals: refusals.size,
    aiBudgetExhausted: exhausted.size,
    legalGaps,
    now,
  });
}
